/**
 * Finance wording shared by the Cloud Office admin and the client portal, so the same word means
 * the same thing on both sides (Sales / Purchases split used by Zoho Books, QuickBooks).
 */

export const FINANCE_SECTIONS = {
  invoices: {
    label: "Invoices",
    hint: "Bills you send to your clients",
  },
  receivables: {
    label: "Receivables",
    hint: "Money your clients still owe you",
  },
  paymentsReceived: {
    label: "Payments Received",
    hint: "Money that has come in",
  },
  payables: {
    label: "Payables",
    hint: "Bills you need to pay",
  },
  settings: {
    label: "Finance Settings",
    hint: "Bill To companies, bank accounts and invoice defaults",
  },
};

/** In the client portal, Payables are the bills Cloud Office sends the client. */
export const PORTAL_PAYABLES_HINT = "Bills from Cloud Office that you need to pay";

/** Admin sidebar page id -> route. */
export const ADMIN_FINANCE_PATHS = {
  FinanceInvoices: "/finance/invoices",
  FinanceReceivables: "/finance/receivables",
  FinancePaymentsReceived: "/finance/payments-received",
  FinancePayables: "/finance/payables",
  FinanceSettings: "/finance/settings",
};

export const ADMIN_FINANCE_PAGE_SECTIONS = {
  FinanceInvoices: FINANCE_SECTIONS.invoices,
  FinanceReceivables: FINANCE_SECTIONS.receivables,
  FinancePaymentsReceived: FINANCE_SECTIONS.paymentsReceived,
  FinancePayables: FINANCE_SECTIONS.payables,
  FinanceSettings: FINANCE_SECTIONS.settings,
};

/** Sidebar page id for an admin finance path (legacy /finance-management/* included), else null. */
export function adminFinancePageForPath(path) {
  if (path.startsWith("/finance/invoices")) return "FinanceInvoices";
  if (path.startsWith("/finance/receivables")) return "FinanceReceivables";
  if (path.startsWith("/finance/payments-received")) return "FinancePaymentsReceived";
  if (path.startsWith("/finance/payables")) return "FinancePayables";
  if (path.startsWith("/finance/settings") || path.startsWith("/finance-management/")) return "FinanceSettings";
  if (path === "/finance-management") return "FinanceInvoices";
  return null;
}

/** Finance Settings tabs (admin). Keys double as the ?tab= value. */
export const SETTINGS_TABS = {
  billTo: "bill-to",
  ourAccounts: "our-bank-accounts",
  clientAccounts: "client-bank-accounts",
};

export function settingsPath(tab) {
  return `${ADMIN_FINANCE_PATHS.FinanceSettings}?tab=${tab}`;
}

/** Plain-English status shown for invoices and bills everywhere. */
const STATUS_LABELS = {
  draft: "Draft",
  sent: "Unpaid",
  pending: "Unpaid",
  unpaid: "Unpaid",
  issued: "Unpaid",
  open: "Unpaid",
  "partially paid": "Partially Paid",
  "part paid": "Partially Paid",
  paid: "Paid",
  overdue: "Overdue",
  cancelled: "Cancelled",
};

const STATUS_TONES = {
  Draft: "neutral",
  Unpaid: "info",
  "Partially Paid": "warning",
  Paid: "success",
  Overdue: "danger",
  Cancelled: "neutral",
};

function parseIsoDate(value) {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Days past the due date (0 when not yet due or no due date). */
export function daysOverdue(dueDate, today = startOfToday()) {
  const due = parseIsoDate(dueDate);
  if (!due) return 0;
  return Math.max(0, Math.round((today - due) / 86400000));
}

function isSettled(status) {
  const s = String(status || "").toLowerCase();
  return s === "paid" || s === "cancelled" || s === "draft";
}

/** True when money is still owed and the due date has passed. */
export function isOverdue(record) {
  if (!record) return false;
  if (String(record.Status || "").toLowerCase() === "overdue") return true;
  if (isSettled(record.Status)) return false;
  if (Number(record.Balance_Amount || 0) <= 0.001) return false;
  return daysOverdue(record.Due_Date) > 0;
}

/** Display label for an invoice/bill record ({ Status, Due_Date, Balance_Amount }). */
export function financeStatusLabel(record) {
  if (isOverdue(record)) return "Overdue";
  const raw = String(record?.Status || "").trim();
  return STATUS_LABELS[raw.toLowerCase()] || raw || "—";
}

export function financeStatusTone(label) {
  return STATUS_TONES[label] || "neutral";
}

/** Overdue aging buckets used by both Receivables pages and admin Payables. */
export const AGING_BUCKETS = [
  { key: "current", label: "Not due yet" },
  { key: "d1_30", label: "1-30 days late" },
  { key: "d31_60", label: "31-60 days late" },
  { key: "d61_90", label: "61-90 days late" },
  { key: "d90_plus", label: "90+ days late" },
];

export function agingBucketKey(dueDate, today = startOfToday()) {
  const late = daysOverdue(dueDate, today);
  if (late <= 0) return "current";
  if (late <= 30) return "d1_30";
  if (late <= 60) return "d31_60";
  if (late <= 90) return "d61_90";
  return "d90_plus";
}

export function emptyAging() {
  return AGING_BUCKETS.reduce((acc, b) => ({ ...acc, [b.key]: 0 }), {});
}

/** True when the due date falls within the next `days` days (today included). */
export function isDueWithin(dueDate, days, today = startOfToday()) {
  const due = parseIsoDate(dueDate);
  if (!due) return false;
  const diff = Math.round((due - today) / 86400000);
  return diff >= 0 && diff <= days;
}
